import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LinkIcon from "@mui/icons-material/Link";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

/**
 * Premium pill for Associated Item column. Shows first associated asset tag; clickable to navigate.
 * Theme-aware: link-style when present, muted when none.
 */
export default function AssociatedItemPill({ label, onClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const greenColor = theme.palette.success?.main || "#A0C46D";
  const hasLink = Boolean(label && onClick);

  const pillStyles = hasLink
    ? {
        backgroundColor: isDark ? "rgba(96, 165, 250, 0.18)" : "rgba(59, 130, 246, 0.12)",
        color: isDark ? theme.palette.info.light : theme.palette.info.dark,
        border: isDark
          ? "1px solid rgba(96, 165, 250, 0.3)"
          : "1px solid rgba(59, 130, 246, 0.35)",
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.15)"
          : "0 1px 2px rgba(59, 130, 246, 0.08)",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: isDark ? "rgba(96, 165, 250, 0.28)" : "rgba(59, 130, 246, 0.2)",
          borderColor: isDark ? "rgba(96, 165, 250, 0.5)" : "rgba(59, 130, 246, 0.5)",
        },
      }
    : {
        backgroundColor: isDark ? "hsl(215, 15%, 28%)" : "rgba(100, 116, 139, 0.12)",
        color: isDark ? "rgba(255,255,255,0.6)" : theme.palette.text.secondary,
        border: isDark
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(100, 116, 139, 0.2)",
        boxShadow: isDark ? "0 1px 2px rgba(0,0,0,0.12)" : "0 1px 2px rgba(0,0,0,0.04)",
        cursor: "default",
      };

  const Icon = hasLink ? LinkIcon : RemoveCircleOutlineIcon;
  const displayLabel = label && label.trim() !== "" ? label : "—";

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        component="span"
        role={hasLink ? "button" : undefined}
        onClick={hasLink ? onClick : undefined}
        onKeyDown={(e) => {
          if (hasLink && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onClick();
          }
        }}
        tabIndex={hasLink ? 0 : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "center",
          gap: "6px",
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingTop: "3px",
          paddingBottom: "3px",
          borderRadius: "9999px",
          fontSize: "0.75rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
          ...pillStyles,
          ...(hasLink && {
            "&:focus-visible": {
              outline: "none",
              boxShadow: `0 0 0 2px ${greenColor}40`,
            },
          }),
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "0.875rem",
            opacity: 1,
            margin: 0,
            flexShrink: 0,
            color: "inherit",
          },
        }}
      >
        <Icon fontSize="inherit" />
        {displayLabel}
      </Box>
    </Box>
  );
}
